/**
 * The five things a door can scan (docs: design_handoff_door_scanner/README.md,
 * "Contexts"). One scanner, driven by `kind`: the chip, the stat labels, the
 * noun in the copy and the word a good scan flashes.
 *
 * `event` scans the event door; `gathering` (Oct 2026) scans the gathering's own door through
 * `gatheringService.doorScan / doorCheckIn` — a gathering seat never resolves on an event door
 * and an event ticket never resolves on the gathering's. Portal / signal / offer still need
 * their own door API before a route mounts them.
 */
export const DOOR_KINDS = {
  event: { label: 'EVENT', bg: '#FAF42E', stats: ['IN', 'SOLD', 'LEFT'], noun: 'ticket', ok: 'VERIFIED', counted: 'IN' },
  portal: { label: 'PORTAL', bg: '#FFCCE6', stats: ['IN', 'GOING', 'LEFT'], noun: 'invite', ok: 'VERIFIED', counted: 'IN' },
  signal: { label: 'SIGNAL', bg: '#03FFFF', stats: ['IN', 'PASSES', 'LEFT'], noun: 'pass', ok: 'VERIFIED', counted: 'IN' },
  gathering: { label: 'GATHERING', bg: '#A2E580', stats: ['IN', 'REGISTERED', 'LEFT'], noun: 'badge', ok: 'VERIFIED', counted: 'IN' },
  offer: { label: 'PRO OFFER', bg: '#C6B8FF', stats: ['REDEEMED', 'CLAIMED', 'LEFT'], noun: 'offer', ok: 'REDEEMED', counted: 'REDEEMED' },
};

/** The roster scopes, in the order of the stat tiles. */
export const SCOPES = ['in', 'sold', 'left'];

export const doorKind = (kind) => DOOR_KINDS[kind] || DOOR_KINDS.event;

/** `TICKET` / `TICKETS` — uppercase, plural past one. */
export const nounFor = (k, n) => {
  const noun = k.noun.toUpperCase();
  if (n === 1) return noun;
  return noun.endsWith('S') ? `${noun}ES` : `${noun}S`;
};

/** Result tones: the flash, and the tag on a session-log row. */
export const TONES = {
  ok: { bg: '#03FF00', ink: '#000', tag: 'IN' },
  // Offline: recorded, not yet ruled on. Deliberately NOT green — nobody should read a queued
  // scan as a verified one.
  queued: { bg: '#00FFFF', ink: '#000', tag: 'SAVED' },
  dup: { bg: '#FFCC00', ink: '#000', tag: 'DUP' },
  no: { bg: '#FE0100', ink: '#fff', tag: 'NO' },
};

/**
 * DoorScanResultDto (or a client-side refusal) → what the frame flashes and
 * the session log records.
 */
export function describeResult(k, r) {
  const qty = r.ticketsInPurchase || r.quantity || 1;
  const name = r.guestName || '';
  switch (r.result) {
    case 'queued':
      return { tone: 'queued', title: 'SAVED OFFLINE', name, sub: 'LET THEM IN — IT SYNCS WHEN YOU ARE BACK' };
    case 'ok': {
      const n = r.checkedInCount ?? r.ticketNumber ?? qty;
      // `synced` means this is a queued scan the server has now accepted, arriving long after
      // the person walked in — say so, or it reads as somebody scanning again.
      return { tone: 'ok', title: r.synced ? 'SYNCED' : k.ok, name, sub: `${n}/${qty} ${nounFor(k, qty)} IN` };
    }
    case 'already':
      return { tone: 'dup', title: 'ALREADY IN', name, sub: `${qty}/${qty} ${nounFor(k, qty)} USED` };
    case 'wrong_event':
    case 'wrong_gathering':
      return { tone: 'no', title: `WRONG ${k.label}`, name, sub: `NOT A ${k.noun.toUpperCase()} FOR THIS ${k.label}` };
    case 'closed':
      return { tone: 'no', title: 'DOOR CLOSED', name, sub: `THIS ${k.label} ISN'T ADMITTING RIGHT NOW` };
    case 'transferred':
      return { tone: 'no', title: 'SEAT TRANSFERRED', name, sub: 'SCAN THE HOLDER\'S OWN CODE' };
    case 'reconciliation_required':
      return { tone: 'no', title: 'NEEDS A LOOK', name, sub: 'THIS ENTRY\'S SEATS NEED RECONCILING FIRST' };
    case 'wrong_kind':
      return { tone: 'no', title: 'AN OFFER', name: '', sub: 'SCAN OFFERS FROM THE PRO DASHBOARD' };
    default:
      return {
        tone: 'no',
        title: 'NO ENTRY',
        name: name || (r.message && !/^not a portal/i.test(r.message) ? r.message : ''),
        sub: `NOT A ${k.noun.toUpperCase()} FOR THIS ${k.label}`,
      };
  }
}

/** `2 TICKETS · NOT HERE YET` / `1/2 IN · 1 STILL TO SCAN` / `2/2 IN`. */
export function rosterMeta(k, row) {
  const qty = Math.max(1, row.quantity || 1);
  const c = row.checkedInCount ?? (row.checkedInAt ? qty : 0);
  if (c <= 0) return `${qty} ${nounFor(k, qty)} · NOT HERE YET`;
  if (c < qty) return `${c}/${qty} IN · ${qty - c} STILL TO SCAN`;
  return `${c}/${qty} IN`;
}
